import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Icon,
  List,
  ListIcon,
  ListItem,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaChevronRight,
  FaPrint,
  FaShippingFast,
  FaStore,
} from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  formatMerchShippingAddress,
  getMerchOrderConfirmationMessage,
  getMerchOrderConfirmationTitle,
  getMerchOrderNextSteps,
} from '../../utils/merchFulfillmentLabels';
import {
  printMerchOrderReceipt,
  resolveMerchReceiptCustomer,
} from '../../utils/merchOrderReceiptPdf';
import { resolveMerchOrderDocumentId } from '../../utils/merchOrderCheckout';

function MerchOrderSuccessContent({ summary }) {
  const { userData } = useAuth();
  const toast = useToast();
  const [isPrinting, setIsPrinting] = useState(false);
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.400');

  const fulfillmentMethod =
    summary?.fulfillmentMethod === 'shipping' ? 'shipping' : 'pickup';
  const isPreorder = summary?.isPreorder === true;
  const isShipping = fulfillmentMethod === 'shipping';
  const shippingAddress = formatMerchShippingAddress(summary?.shippingAddress);

  const title = getMerchOrderConfirmationTitle({ isPreorder });
  const message = getMerchOrderConfirmationMessage({
    isPreorder,
    fulfillmentMethod,
  });
  const nextSteps = getMerchOrderNextSteps({ isPreorder, fulfillmentMethod });

  const handlePrintReceipt = async () => {
    try {
      setIsPrinting(true);

      let refreshedUser = userData;
      try {
        const response = await axios.get('/api/users/me', {
          withCredentials: true,
        });
        refreshedUser = response.data || userData;
      } catch {
        // usa userData già in memoria
      }

      const orderDocumentId = resolveMerchOrderDocumentId(
        summary,
        refreshedUser
      );
      const matchedOrder = refreshedUser?.orders?.find(
        (order) => String(order.documentId) === String(orderDocumentId)
      );

      const customer = resolveMerchReceiptCustomer({
        order: matchedOrder,
        summary,
        userData: refreshedUser,
      });

      await printMerchOrderReceipt(
        {
          ...summary,
          orderDocumentId,
          documentId: orderDocumentId,
        },
        customer
      );
    } catch (error) {
      toast({
        title: 'Errore stampa ricevuta',
        description: error.message || 'Impossibile generare la ricevuta.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Box bg={bgColor} minH="100vh" py={{ base: 8, md: 12 }}>
      <Container maxW="container.md">
        <VStack spacing={8} align="stretch">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaCheckCircle} boxSize={16} color="green.500" />
            <Heading size="lg">{title}</Heading>
            <Text fontSize="lg" color={muted} maxW="540px">
              {message}
            </Text>
          </VStack>

          <Box
            p={6}
            bg={cardBg}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="sm"
          >
            <VStack align="stretch" spacing={4}>
              <HStack spacing={3}>
                <Icon
                  as={isShipping ? FaShippingFast : FaStore}
                  color="teal.500"
                  boxSize={5}
                />
                <Heading size="sm">
                  {isShipping
                    ? 'Spedizione a domicilio'
                    : 'Ritiro in sede / evento'}
                </Heading>
              </HStack>

              <Divider />

              <VStack align="stretch" spacing={2}>
                <Text fontSize="sm" color={muted}>
                  Prodotto
                </Text>
                <Text fontWeight="semibold">{summary.productName}</Text>
              </VStack>

              {(summary.shippingFee > 0 || summary.discountAmount > 0) && (
                <VStack align="stretch" spacing={1} fontSize="sm">
                  {summary.discountAmount > 0 && (
                    <HStack justify="space-between">
                      <Text color={muted}>Prodotto</Text>
                      <Text>
                        €{Number(summary.productTotal || 0).toFixed(2)}
                      </Text>
                    </HStack>
                  )}
                  {summary.shippingFee > 0 && (
                    <HStack justify="space-between">
                      <Text color={muted}>Spedizione</Text>
                      <Text>€{Number(summary.shippingFee).toFixed(2)}</Text>
                    </HStack>
                  )}
                </VStack>
              )}

              <HStack justify="space-between">
                <Text fontWeight="semibold">Totale pagato</Text>
                <Text fontSize="xl" fontWeight="bold" color="green.600">
                  €{Number(summary.total || 0).toFixed(2)}
                </Text>
              </HStack>

              {isShipping && shippingAddress && (
                <Box pt={2}>
                  <Text fontSize="sm" color={muted} mb={1}>
                    Indirizzo di spedizione
                  </Text>
                  <Text fontSize="sm">{shippingAddress}</Text>
                </Box>
              )}
            </VStack>
          </Box>

          <Box
            p={6}
            bg={cardBg}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
          >
            <Heading size="sm" mb={4}>
              Prossimi passi
            </Heading>
            <List spacing={3}>
              {nextSteps.map((step) => (
                <ListItem key={step} fontSize="sm" color={muted}>
                  <ListIcon as={FaChevronRight} color="teal.500" />
                  {step}
                </ListItem>
              ))}
            </List>
          </Box>

          <Text fontSize="sm" color={muted} textAlign="center">
            Per dettagli e aggiornamenti sull&apos;ordine vai in{' '}
            <Text
              as={RouterLink}
              to="/account/ordini"
              color="teal.600"
              fontWeight="semibold"
            >
              Account → I tuoi ordini
            </Text>
            . Per assistenza contattaci via{' '}
            <Text as="a" href="mailto:info@cilassociazione.it" color="teal.600">
              info@cilassociazione.it
            </Text>
            .
          </Text>

          <VStack spacing={3}>
            <Button
              leftIcon={<FaPrint />}
              colorScheme="blue"
              variant="outline"
              width="full"
              onClick={handlePrintReceipt}
              isLoading={isPrinting}
              loadingText="Preparazione ricevuta..."
            >
              Stampa ricevuta
            </Button>
            <Button
              as={RouterLink}
              to="/account/ordini"
              colorScheme="teal"
              width="full"
            >
              Vai ai tuoi ordini
            </Button>
            {summary.productId && (
              <Button
                as={RouterLink}
                to={`/merch/product/${summary.productId}`}
                variant="outline"
                width="full"
              >
                Torna al prodotto
              </Button>
            )}
            <Button as={RouterLink} to="/merch" variant="ghost" width="full">
              Torna al catalogo merch
            </Button>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
}

export default MerchOrderSuccessContent;
